import { Button } from '@/components/ui/button'
import { isArenaHost } from '@/lib/arena'
import { getStartedGameRoute } from '@/lib/started-game-route'
import { useI18n } from '@/lib/i18n'
import { useGameAttention } from '@/lib/game-attention'
import {
  formatGameSettingDisplay,
  formatGameSettingGroups,
  parseGameSettingToPlayerConfig,
} from '@/lib/play'
import { statusI18nSegment } from '@/lib/room-status-label'
import { UserGame } from '@/lib/types.ts'
import { cn, formatDate } from '@/lib/utils'
import { readRoster, readSettingMode } from '@repo/sdk'
import { useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

const GAME_STATUS_FILTERS = [
  { value: 'all', labelKey: 'dashboard.games.filters.all' },
  { value: 'awaiting', labelKey: 'gameAttention.awaitingInput' },
  { value: 'active', labelKey: 'dashboard.games.filters.inProgress' },
  { value: 'completed', labelKey: 'dashboard.games.filters.completed' },
  { value: 'error', labelKey: 'dashboard.games.filters.error' },
] as const

const GAME_MODE_FILTERS = [
  { value: 'all', labelKey: 'dashboard.games.filters.all' },
  { value: 'solo', labelKey: 'dashboard.games.filters.solo' },
  { value: 'multiplayer', labelKey: 'dashboard.games.filters.multiplayer' },
] as const

type GameStatusFilter = typeof GAME_STATUS_FILTERS[number]['value']
type GameModeFilter = typeof GAME_MODE_FILTERS[number]['value']

const GAME_STATUS_FILTER_VALUES = new Set<string>(
  GAME_STATUS_FILTERS.map(filter => filter.value),
)
const GAME_MODE_FILTER_VALUES = new Set<string>(
  GAME_MODE_FILTERS.map(filter => filter.value),
)

function isGameStatusFilter(value: string | null): value is GameStatusFilter {
  return !!value && GAME_STATUS_FILTER_VALUES.has(value)
}

function isGameModeFilter(value: string | null): value is GameModeFilter {
  return !!value && GAME_MODE_FILTER_VALUES.has(value)
}

function isMultiplayerGame(game: UserGame) {
  return game.recordType === 'room' || !!game.roomId
}

function matchesGameStatusFilter(game: UserGame, filter: GameStatusFilter) {
  if (filter === 'all')
    return true
  if (filter === 'active')
    return !['completed', 'error', 'cancelled'].includes(game.status)
  if (filter === 'awaiting')
    return game.isAwaitingInput
  return game.status === filter
}

function matchesGameModeFilter(game: UserGame, filter: GameModeFilter) {
  if (filter === 'all')
    return true
  if (filter === 'solo')
    return !isMultiplayerGame(game)
  if (filter === 'multiplayer')
    return isMultiplayerGame(game)
  return true
}

export function renderGameSetting(gameSetting: string | null | undefined, t: (key: string) => string) {
  if (!gameSetting)
    return '-'

  const settingMode = readSettingMode(gameSetting)
  if (settingMode.markers.containsR) {
    const genericKey = settingMode.declaredPrefix?.kind === 'D' && settingMode.declaredPrefix.digits === '10'
      ? 'gameSettings.D10_ULTIMATE_TRIAL'
      : 'gameSettings.G10_ULTIMATE_TRIAL'
    return t(genericKey)
  }

  const { displayStyle } = readRoster({ kind: 'selection', setting: gameSetting })
  if (displayStyle === 'title')
    return t(`gameSettings.${gameSetting}`)

  const parsed = parseGameSettingToPlayerConfig(gameSetting)
  if (parsed) {
    if (displayStyle === 'inline')
      return formatGameSettingDisplay(parsed, t)

    const groups = formatGameSettingGroups(parsed, t)
    return (
      <span className="flex flex-col gap-1">
        <span>
          <span className="font-medium text-muted-foreground">{t('game.goodFaction')}: </span>
          {groups.good}
        </span>
        <span>
          <span className="font-medium text-muted-foreground">{t('game.evilFaction')}: </span>
          {groups.evil}
        </span>
      </span>
    )
  }

  const translationKey = `gameSettings.${gameSetting}`
  const translated = t(translationKey)
  if (translated !== translationKey) {
    return translated
  }

  return gameSetting
}

export function getGameMode(game: Pick<UserGame, 'gameSetting' | 'recordType' | 'roomId'>, t: (key: string) => string): string {
  const { gameSetting } = game
  if (!gameSetting)
    return '-'
  const settingMode = readSettingMode(gameSetting)
  const mode = settingMode.mode === 'comparison'
    ? t('gameSettings.MODEL_COMPARISON')
    : settingMode.terminalSuffix === 'W'
      ? t('gameSettings.WATCH_MODE')
      : settingMode.terminalSuffix === 'B'
        ? t('gameSettings.BYOM')
        : settingMode.markers.containsH
          ? t('gameSettings.HUMAN_VS_AI')
          : settingMode.markers.containsR
            ? t('gameSettings.G10_ULTIMATE_TRIAL')
            : t('dashboard.games.standard') || 'Standard'
  const playMode = game.recordType === 'room' || game.roomId
    ? t('dashboard.games.playModes.multiplayer')
    : t('dashboard.games.playModes.solo')
  return `${mode}${t('dashboard.games.modeSeparator')}${playMode}`
}

function renderGameMode(game: UserGame, t: (key: string) => string) {
  const label = getGameMode(game, t)
  if (!game.hasSheriff)
    return label

  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden="true">🎖️</span>
      <span>{label}</span>
    </span>
  )
}

function getBillingModeBadge(game: UserGame, t: (key: string) => string) {
  const label = game.isFreeGame
    ? t('game.freeMode')
    : game.isHighPerformance
      ? t('game.expertMode')
      : t('game.quickMode')
  const className = game.isFreeGame
    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
    : game.isHighPerformance
      ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
      : 'bg-sky-500/10 text-sky-600 dark:text-sky-400'

  return (
    <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', className)}>
      {label}
    </span>
  )
}

function CostSplitLines({ game, t }: {
  game: UserGame
  t: (key: string) => string
}) {
  const money = (microdollars: number) => `$${(microdollars / 1_000_000).toFixed(4)}`
  return (
    <div className="space-y-0.5 text-xs font-normal" data-testid="game-cost-split-detail">
      <div className="flex items-baseline justify-end gap-2">
        <span className="text-muted-foreground">{t('dashboard.games.costTotal')}</span>
        <span className="font-mono">{money(game.gameTotalCost)}</span>
      </div>
      <div className="flex items-baseline justify-end gap-2">
        <span className="text-muted-foreground">{t('dashboard.games.costYours')}</span>
        <span className="font-mono text-amber-500">{money(game.gameCost)}</span>
      </div>
    </div>
  )
}

export function GameTable({ games }: { games?: UserGame[] }) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useI18n()
  const attention = useGameAttention()
  const statusFilterParam = searchParams.get('status')
  const modeFilterParam = searchParams.get('mode')
  const legacyActive = ['lobby', 'paused', 'in_progress'].includes(statusFilterParam ?? '')
  const statusFilter = legacyActive
    ? 'active'
    : isGameStatusFilter(statusFilterParam) ? statusFilterParam : 'all'
  const modeFilter = isGameModeFilter(modeFilterParam) ? modeFilterParam : 'all'

  useEffect(() => {
    if (!legacyActive)
      return
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('status', 'active')
    setSearchParams(nextParams, { replace: true })
  }, [legacyActive, searchParams, setSearchParams])

  const filteredGames = useMemo(() => {
    const awaitingIds = new Set(attention.data?.awaitingGameIds ?? [])
    return (games ?? []).map(game => ({
      ...game,
      isAwaitingInput: game.recordType === 'game' && awaitingIds.has(game.id),
    })).filter(game =>
      matchesGameStatusFilter(game, statusFilter)
      && matchesGameModeFilter(game, modeFilter),
    )
  }, [attention.data?.awaitingGameIds, games, modeFilter, statusFilter])

  const formatCost = (microdollars: number) => {
    return `$${(microdollars / 1_000_000).toFixed(4)}`
  }

  const isSplit = (game: UserGame) =>
    (game.payerCount ?? 1) > 1 && !game.isFreeGame && !!game.gameTotalCost

  const getGameRoute = (game: UserGame) => {
    if (game.roomId)
      return `/room/${game.roomId}`
    return getStartedGameRoute(game.id, game.gameSetting ?? '')
  }

  const isCompleted = (game: UserGame) => game.status === 'completed'
  const getViewRoute = (game: UserGame) => {
    const route = getStartedGameRoute(game.id, game.gameSetting ?? '')
    return route.startsWith('/parallel/') ? route : `${route}?view=god`
  }
  const getReviewRoute = (game: UserGame) =>
    isArenaHost() ? `/review/${game.id}` : `/demo/${game.id}`

  const handleStatusFilterChange = (value: GameStatusFilter) => {
    const nextParams = new URLSearchParams(searchParams)
    if (value === 'all') {
      nextParams.delete('status')
    }
    else {
      nextParams.set('status', value)
    }
    setSearchParams(nextParams, { replace: true })
  }

  const handleModeFilterChange = (value: GameModeFilter) => {
    const nextParams = new URLSearchParams(searchParams)
    if (value === 'all') {
      nextParams.delete('mode')
    }
    else {
      nextParams.set('mode', value)
    }
    setSearchParams(nextParams, { replace: true })
  }

  return (
    <div className="min-w-0 space-y-3">
      <div className="space-y-2">
        <div className="space-y-1" data-testid="game-status-filter">
          <span className="text-xs font-medium text-muted-foreground">{t('dashboard.games.filterGroups.status')}</span>
          <div className="flex flex-wrap gap-2 pb-1">
            {GAME_STATUS_FILTERS.map(filter => (
              <Button
                key={filter.value}
                type="button"
                size="sm"
                variant={statusFilter === filter.value ? 'default' : 'outline'}
                aria-pressed={statusFilter === filter.value}
                onClick={() => handleStatusFilterChange(filter.value)}
                data-testid={`game-status-filter-${filter.value}`}
                className="h-8 rounded-md px-3 text-xs"
              >
                {t(filter.labelKey)}
              </Button>
            ))}
          </div>
        </div>
        <div className="space-y-1" data-testid="game-mode-filter">
          <span className="text-xs font-medium text-muted-foreground">{t('dashboard.games.filterGroups.mode')}</span>
          <div className="flex flex-wrap gap-2 pb-1">
            {GAME_MODE_FILTERS.map(filter => (
              <Button
                key={filter.value}
                type="button"
                size="sm"
                variant={modeFilter === filter.value ? 'default' : 'outline'}
                aria-pressed={modeFilter === filter.value}
                onClick={() => handleModeFilterChange(filter.value)}
                data-testid={`game-mode-filter-${filter.value}`}
                className="h-8 rounded-md px-3 text-xs"
              >
                {t(filter.labelKey)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-3 md:hidden" data-testid="mobile-game-card-list">
        {filteredGames.map(game => (
          <div
            key={game.id}
            className="rounded-lg border bg-card p-3 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  {getStatusBadge(game.status, t, game.isAwaitingInput)}
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5 text-xs font-medium',
                      game.isOwned
                        ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400'
                        : 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
                    )}
                  >
                    {game.isOwned ? t('auth.dashboard.gameOwnedBadge') : t('auth.dashboard.gameJoinedBadge')}
                  </span>
                  {getBillingModeBadge(game, t)}
                </div>
                <p className="line-clamp-2 text-sm font-medium">
                  {renderGameMode(game, t)}
                </p>
              </div>
              {isCompleted(game) ? (
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(getViewRoute(game))}
                  >
                    {t('dashboard.games.actions.view')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate(getReviewRoute(game))}
                  >
                    {t('nav.review')}
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(getGameRoute(game))}
                  className="shrink-0"
                >
                  {game.recordType === 'room' ? t('dashboard.games.actions.open') : t('dashboard.games.actions.play')}
                </Button>
              )}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3 border-t pt-3 text-xs">
              <div className="min-w-0">
                <p className="text-muted-foreground">{t('dashboard.games.headers.created')}</p>
                <p className="mt-1 truncate font-medium">{formatDate(game.createdAt)}</p>
              </div>
              <div className="min-w-0 text-right">
                <p className="text-muted-foreground">{t('dashboard.games.headers.cost')}</p>
                {isSplit(game)
                  ? <div className="mt-1"><CostSplitLines game={game} t={t} /></div>
                  : (
                      <div className="mt-1 font-mono text-sm text-amber-500">
                        {game.isFreeGame
                          ? formatCost(game.inputCost + game.outputCost)
                          : formatCost(game.gameCost)}
                      </div>
                    )}
              </div>
            </div>
          </div>
        ))}
        {filteredGames.length === 0 && (
          <div className="flex min-h-32 items-center justify-center rounded-lg border border-dashed px-4 py-8">
            <p className="text-sm text-muted-foreground">{t(games?.length ? 'dashboard.games.emptyFilter' : 'dashboard.games.empty')}</p>
          </div>
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-lg border md:block">
        <table className="w-full">
          <thead className="border-b bg-muted/50">
            <tr>
              <th className="min-w-28 whitespace-nowrap p-3 text-left text-sm font-medium">{t('dashboard.games.headers.status')}</th>
              <th className="p-3 text-left text-sm font-medium">{t('dashboard.games.headers.setting')}</th>
              <th className="p-3 text-left text-sm font-medium">{t('dashboard.games.headers.gameMode')}</th>
              <th className="p-3 text-left text-sm font-medium">{t('dashboard.games.headers.language')}</th>
              <th className="p-3 text-right text-sm font-medium">{t('dashboard.games.headers.inputTokens')}</th>
              <th className="p-3 text-right text-sm font-medium">{t('dashboard.games.headers.outputTokens')}</th>
              <th className="p-3 text-right text-sm font-medium">{t('dashboard.games.headers.cost')}</th>
              <th className="p-3 text-left text-sm font-medium">{t('dashboard.games.headers.created')}</th>
              <th className="p-3 text-left text-sm font-medium">{t('dashboard.games.headers.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredGames.map(game => (
              <tr
                key={game.id}
                className="cursor-pointer border-b transition-colors hover:bg-muted/50"
                onClick={() => {
                  navigate(isCompleted(game) ? getViewRoute(game) : getGameRoute(game))
                }}
              >
                <td className="min-w-28 whitespace-nowrap p-3">{getStatusBadge(game.status, t, game.isAwaitingInput)}</td>
                <td className="p-3 text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    {renderGameSetting(game.gameSetting, t)}
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-xs font-medium',
                        game.isOwned
                          ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400'
                          : 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
                      )}
                    >
                      {game.isOwned ? t('auth.dashboard.gameOwnedBadge') : t('auth.dashboard.gameJoinedBadge')}
                    </span>
                    {getBillingModeBadge(game, t)}
                  </span>
                </td>
                <td className="p-3 text-sm">{renderGameMode(game, t)}</td>
                <td className="p-3 text-sm uppercase">{game.language}</td>
                <td className="p-3 text-right font-mono text-sm text-muted-foreground">
                  {game.inputTokens > 0 ? game.inputTokens.toLocaleString() : '—'}
                </td>
                <td className="p-3 text-right font-mono text-sm text-muted-foreground">
                  {game.outputTokens > 0 ? game.outputTokens.toLocaleString() : '—'}
                </td>
                <td className="p-3 text-right font-mono text-sm text-amber-400">
                  {isSplit(game)
                    ? <CostSplitLines game={game} t={t} />
                    : game.isFreeGame
                      ? <span className="text-muted-foreground">{formatCost(game.inputCost + game.outputCost)}</span>
                      : formatCost(game.gameCost)}
                </td>
                <td className="p-3 text-sm">{formatDate(game.createdAt)}</td>
                <td className="p-3">
                  {isCompleted(game) ? (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(getViewRoute(game))
                        }}
                      >
                        {t('dashboard.games.actions.view')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(getReviewRoute(game))
                        }}
                      >
                        {t('nav.review')}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(getGameRoute(game))
                      }}
                    >
                      {game.recordType === 'room' ? t('dashboard.games.actions.open') : t('dashboard.games.actions.play')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredGames.length === 0 && (
          <div className="flex min-h-32 items-center justify-center border-t px-4 py-8">
            <p className="text-sm text-muted-foreground">{t(games?.length ? 'dashboard.games.emptyFilter' : 'dashboard.games.empty')}</p>
          </div>
        )}
      </div>
    </div>
  )
}

function getStatusBadge(status: string, t: (key: string) => string, awaitingInput = false) {
  const statusColors: Record<string, string> = {
    in_progress: 'bg-blue-500/10 text-blue-500',
    lobby: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    playing: 'bg-blue-500/10 text-blue-500',
    completed: 'bg-green-500/10 text-green-500',
    error: 'bg-red-500/10 text-red-500',
    paused: 'bg-yellow-500/10 text-yellow-500',
  }
  return (
    <span
      className={cn('rounded-full px-2 py-1 text-xs font-medium', statusColors[status] || 'bg-gray-500/10 text-gray-500')}
    >
      {awaitingInput
        ? t('gameAttention.awaitingInput')
        : t(`dashboard.games.statuses.${statusI18nSegment(status)}`) || status.replace('_', ' ')}
    </span>
  )
}
